import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spin, Radio } from 'antd';

import FormsTable from '../FormsTable';
import FormResponder from '../FormResponder';
import FormSearchInput from '../FormSearchInput';
import {
  favoriteFormTemplate,
  getAssignedFormTemplates,
} from '../state/forms.actions';

import { getProjectIdMap } from '../../state/selectors';
import { getManualFormColumns } from '../FormColumns';
import {
  handleFormClick,
  getManualFormsTableData,
  MANUAL_FAVORITE_TAB,
  MANUAL_GLOBAL_TAB,
  MANUAL_PROJECT_TAB,
  getFilters,
} from '../formHelpers';
import { FORMS_HOME_PREVIEW_LIMIT } from './formsHomeHelpers';
import OnTraccrRadioGroup from '../../common/inputs/OnTraccrRadioGroup';

export default function FormsHomeManualFormSection({ history }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const projectIdMap = useSelector(getProjectIdMap);
  const assignedFormTemplates = useSelector((state) => state.forms.assignedFormTemplates);
  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [tab, setTab] = useState(MANUAL_FAVORITE_TAB);
  const [assignedForm, setAssignedForm] = useState({});
  const [showDetail, setShowDetail] = useState(false);
  const [selectedIsEdit, setSelectedIsEdit] = useState(false);
  const [selectedIsResubmit, setSelectedIsResubmit] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getAssignedFormTemplates());
  }, []);

  useEffect(() => {
    setAssignedForm((prevState) => ({
      ...prevState,
      draftId: latestManualDraftId,
    }));
  }, [latestManualDraftId]);

  const tableData = useMemo(() => (
    getManualFormsTableData({
      assignedFormTemplates,
      tab,
      searchStr,
      projectIdMap,
    })
  ), [
    tab,
    assignedFormTemplates,
    searchStr,
    projectIdMap,
  ]);

  const previewData = useMemo(
    () => tableData.slice(0, FORMS_HOME_PREVIEW_LIMIT),
    [tableData],
  );

  const manualProjectFilters = useMemo(() => getFilters({
    data: previewData,
    idMap: projectIdMap,
    key: 'projectId',
  }), [previewData, projectIdMap]);

  const onFormClick = useCallback(async (record) => {
    await handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setAssignedForm,
      setShowDetail,
      setSelectedIsEdit,
      setSelectedIsResubmit,
    });
  }, [loading]);

  const onSearch = useCallback((e) => {
    const { target: { value } = {} } = e;
    setSearchStr(value);
  }, []);

  const onFavoriteClick = useCallback((e, form = {}) => {
    e.stopPropagation();
    const { id, isFavorite } = form;
    if (!id) return;
    dispatch(favoriteFormTemplate(id, isFavorite));
  }, []);

  const columns = useMemo(() => getManualFormColumns({
    projectIdMap,
    projectFilters: manualProjectFilters,
    tab,
    onFavoriteClick,
    settings,
    t,
  }), [manualProjectFilters, tab, settings, projectIdMap, onFavoriteClick, t]);

  const tabFilterOptions = useMemo(() => [
    { label: 'Favorite', value: MANUAL_FAVORITE_TAB },
    { label: 'Project', value: MANUAL_PROJECT_TAB },
    { label: 'Global', value: MANUAL_GLOBAL_TAB },
  ], []);

  return (
    <section className="forms-home-section">
      <div className="forms-home-section-header">
        <div>
          <h2 className="forms-home-section-title">Start a new form</h2>
          <p className="forms-home-section-subtitle">
            Pick from a favorite, a {t('Project').toLowerCase()}-specific form, or the global library.
          </p>
        </div>
        <div className="forms-home-section-actions">
          <FormSearchInput
            searchStr={searchStr}
            onSearch={onSearch}
            placeholder="Search templates"
            testId="formsHomeStartFormSearch"
          />
          <OnTraccrRadioGroup
            value={tab}
            size="medium"
            buttonStyle="outline"
            className="forms-home-tab-toggle"
            onChange={(newTab) => setTab(newTab)}
          >
            {tabFilterOptions.map(({ label, value }) => (
              <Radio.Button key={value} value={value}>
                {label}
              </Radio.Button>
            ))}
          </OnTraccrRadioGroup>
          <span
            className="forms-home-view-all"
            onClick={() => history.push('/forms/manual')}
          >
            View all →
          </span>
        </div>
      </div>
      <FormsTable
        data={previewData}
        columns={columns}
        onClickRow={onFormClick}
        isHomePreview
      />
      <FormResponder
        visible={showDetail}
        onClose={() => setShowDetail(false)}
        history={history}
        assignedForm={assignedForm}
        isOnEditStep={selectedIsEdit}
        isResubmit={selectedIsResubmit}
      />
      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      )}
    </section>
  );
}

FormsHomeManualFormSection.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};
